import { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { fetchAllResultsWithError, fetchWithError, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams, removeNullUndefined } from "@nens/shared-utils";

import { paginatedSchema } from "../pagination";

export const locationsListItemSchema = z.object({
  url: z.string(),
  uuid: z.string(),
  code: z.string(),
  name: z.string(),
  geometry: z
    .object({
      coordinates: z.array(z.number()),
    })
    .nullable(),
  object: z
    .object({
      id: z.number(),
      type: z.string(),
    })
    .nullable(),
});

export type LocationsListItem = z.infer<typeof locationsListItemSchema>;

export const locationsListSchema = paginatedSchema(locationsListItemSchema);
export type LocationsList = z.infer<typeof locationsListSchema>;

export const locationsSchema = locationsListItemSchema;
export type Location = z.infer<typeof locationsSchema>;

export function useMonitoringNetworkLocations(uuid: string, params = {}) {
  const url = combineUrlAndParams(`/api/v4/monitoringnetworks/${uuid}/locations/`, params);
  return useParsedQuery<LocationsList>(
    ["lizard-api-v4", "monitoringnetworklocations", url],
    () => fetchWithError(url),
    (json: unknown) => locationsListSchema.parse(json)
  );
}

// Use this outside components, so the monitoring network can still
// be stored in cache.
export async function fetchAndCacheMonitoringNetworkLocations(
  queryClient: QueryClient,
  uuid: string,
  params = {}
): Promise<LocationsList | null> {
  try {
    const url = combineUrlAndParams(`/api/v4/monitoringnetworks/${uuid}/locations/`, params);
    const results = await fetchAllResultsWithError<Location[]>(url);

    const monitoringNetworkLocationsList = locationsListSchema.parse({
      count: results.length,
      previous: null,
      next: null,
      results,
    });

    queryClient.setQueryData(
      ["lizard-api-v4", "monitoringnetworklocations", url],
      monitoringNetworkLocationsList
    );
    return monitoringNetworkLocationsList;
  } catch (e) {
    return null;
  }
}

export async function getMonitoringNetworkLocationsFromMonitoringNetwork(
  queryClient: QueryClient,
  monitoringNetworkUuid: string
) {
  // Fetch scenario results
  const resultList = await fetchAndCacheMonitoringNetworkLocations(
    queryClient,
    monitoringNetworkUuid
  );

  // Now check which locations are fetchable by this user
  const locations = removeNullUndefined(
    await Promise.all(resultList ? removeNullUndefined(resultList.results) : [])
  );

  return locations;
}

const getIdFromUrl = (url: string) => {
  // ID is the last part of the URL
  if (url.charAt(url.length - 1) !== "/") {
    // Make sure it ends with '/'
    url += "/";
  }
  const urlParts = url.split("/");
  // urlParts's length is at least = 2
  // Return 2nd last element as number
  return parseInt(urlParts[urlParts.length - 2]);
};

export const findLocationTypeAndId = async (
  location: Location
): Promise<[string, number] | null> => {
  if (!location.object) return null;
  const objectType = location.object.type;
  const objectId = location.object.id;

  // check if object is nested asset
  // currently, we know there are 2 cases of nested asset
  // case 1 is filter with parent asset of groundwater_station
  // case 2 is pump with parent asset of pump_station
  let parentAssetType: string | null = null;
  let parentAssetId: number | null = null;

  if (objectType === "filter") {
    parentAssetType = "groundwaterstation"; // parent asset of filter is groundwater_station
    const parentAssetUrl = await fetch(`/api/v4/filters/${objectId}/`, {
      credentials: "same-origin",
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((asset) => asset.groundwater_station);
    parentAssetId = getIdFromUrl(parentAssetUrl);
  } else if (objectType === "pump") {
    parentAssetType = "pumpstation"; // parent asset of pump is pump_station
    const parentAssetUrl = await fetch(`/api/v4/pumps/${objectId}/`, {
      credentials: "same-origin",
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((asset) => asset.pump_station);
    parentAssetId = getIdFromUrl(parentAssetUrl);
  }

  if (parentAssetType && parentAssetId) {
    return [parentAssetType, parentAssetId];
  } else {
    return [objectType, objectId];
  }
};
